import { useState, useCallback, useMemo } from 'react'

export interface StepConfig<T = any> {
  id: string
  component: React.ComponentType<StepComponentProps<T>>
  canAdvance?: (data: T) => boolean | Promise<boolean>
  canGoBack?: (data: T) => boolean
  onBeforeNext?: (data: T) => Promise<void>
  onBeforeBack?: (data: T) => Promise<void>
}

export interface StepComponentProps<T = any> {
  data: T
  updateData: (updates: Partial<T>) => void
  errors?: Record<string, string>
  isLoading?: boolean
  onNext?: () => Promise<void>
  onExploreFirst?: () => void
  onClose?: () => void
  onMinimize?: () => void
  isLastStep?: boolean
  currentStepIndex?: number
  authUser?: any
  locale?: any
  heatmapData?: any
  stepIndex?: number
  totalSteps?: number
}

export interface UseMultiStepFormOptions<T> {
  steps: StepConfig<T>[]
  initialData: T
  onComplete?: (data: T) => Promise<void>
  initialStepIndex?: number
}

export interface UseMultiStepFormReturn<T> {
  currentStep: StepConfig<T>
  currentStepIndex: number
  totalSteps: number
  formData: T
  isFirstStep: boolean
  isLastStep: boolean
  isLoading: boolean
  errors: Record<string, string>
  updateFormData: (updates: Partial<T>) => void
  goToNext: () => Promise<void>
  goToPrevious: () => void
  goToStep: (index: number) => void
  submitForm: () => Promise<void>
  setErrors: (errors: Record<string, string>) => void
  clearErrors: () => void
}

export const useMultiStepForm = <T extends Record<string, any>>({
  steps,
  initialData,
  onComplete,
  initialStepIndex,
}: UseMultiStepFormOptions<T>): UseMultiStepFormReturn<T> => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex ?? 0)
  const [formData, setFormData] = useState<T>(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const currentStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex])
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1
  const totalSteps = steps.length

  const updateFormData = useCallback((updates: Partial<T>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear errors when data is updated
    setErrors({})
  }, [])

  const clearErrors = useCallback(() => {
    setErrors({})
  }, [])

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStepIndex(index)
      clearErrors()
    }
  }, [steps.length, clearErrors])

  const goToNext = useCallback(async () => {
    try {
      setIsLoading(true)
      clearErrors()

      // Check if we can advance
      if (currentStep.canAdvance) {
        const canProceed = await currentStep.canAdvance(formData)
        if (!canProceed) {
          setErrors({ validation: 'Please complete all required fields' })
          return
        }
      }

      // Execute any pre-navigation logic
      if (currentStep.onBeforeNext) {
        await currentStep.onBeforeNext(formData)
      }

      if (!isLastStep) {
        setCurrentStepIndex(prev => prev + 1)
      }
    } catch (error: any) {
      setErrors({
        general: error.message || 'An error occurred while proceeding to the next step',
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentStep, formData, isLastStep, clearErrors])

  const goToPrevious = useCallback(() => {
    try {
      setIsLoading(true)
      clearErrors()

      // Check if we can go back
      if (currentStep.canGoBack && !(currentStep.canGoBack(formData))) {
        return
      }

      // Execute any pre-navigation logic
      if (currentStep.onBeforeBack) {
        currentStep.onBeforeBack(formData)
      }

      if (!isFirstStep) {
        setCurrentStepIndex(prev => prev - 1)
      }
    } catch (error: any) {
      setErrors({
        general: error.message || 'An error occurred while going back',
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentStep, formData, isFirstStep, clearErrors])

  const submitForm = useCallback(async () => {
    try {
      setIsLoading(true)
      clearErrors()

      // Validate final step
      if (currentStep.canAdvance) {
        const canProceed = await currentStep.canAdvance(formData)
        if (!canProceed) {
          setErrors({ validation: 'Please complete all required fields' })
          return
        }
      }

      // Execute onComplete callback if provided
      if (onComplete) {
        await onComplete(formData)
      }
    } catch (error: any) {
      setErrors({
        submit: error.message || 'An error occurred while submitting the form',
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentStep, formData, onComplete, clearErrors])

  return {
    currentStep,
    currentStepIndex,
    totalSteps,
    formData,
    isFirstStep,
    isLastStep  ,
    isLoading,
    errors,
    updateFormData,
    goToNext,
    goToPrevious,
    goToStep,
    submitForm,
    setErrors,
    clearErrors,
  }
}